import React, { useState } from "react";
import { Plus } from "lucide-react";
import { TEST_IDS } from "@/lib/testIds";

const pains = [
  {
    n: "I.",
    slug: "stressed",
    title: "Financially stressed despite high income",
    body:
      "Earning well, yet money still feels tight, scattered, or unaccounted for at the end of every month. The number on your paycheck doesn’t match how financially secure you feel.",
    expanded:
      "You’re not bad with money. You’re operating without a system. There’s a difference — and it matters. Most people at your income level are still one unexpected expense away from stress. Not because they don’t earn enough. Because everything that comes in already has somewhere to go before it arrives. The paycheck clears. The accounts refill. And somehow, at the end of the month, you’re not sure where it went. That’s not a discipline problem. That’s a structure problem. And structure is fixable.",
  },
  {
    n: "II.",
    slug: "reactive",
    title: "Uncertain and reactive with money",
    body:
      "Financial decisions made in response to circumstances instead of guided by a clear, long-term plan. You’re always catching up — never quite ahead.",
    expanded:
      "Every time you make a financial decision from a place of uncertainty, someone else benefits from your hesitation. The bank charges you interest while you figure it out. The opportunity closes while you wait to feel ready. The bill goes on the card because there was no better plan in place. Reactive isn’t a personality trait. It’s what happens when there’s no system telling you exactly what you can do and when. The people who move with confidence aren’t smarter or luckier. They just know what their money is doing before they need it to do something.",
  },
  {
    n: "III.",
    slug: "dependent",
    title: "Dependent on outside institutions for capital",
    body:
      "Access to liquidity sits with banks, lenders, and financial platforms — not within a structure you own or control. When you need capital, you have to ask for it.",
    expanded:
      "Every time you need capital, you have to apply for it. Qualify for it. Hope someone else decides you deserve it. That’s the system most people are living inside — and they don’t even realize it’s a choice. The bank holds the liquidity. The lender sets the terms. The market decides what your retirement account is worth on the day you need it. You worked for that money. You saved it, earned it, sacrificed for it. And yet when you need to use it, you go through someone else to get it. There’s a different structure. One where the capital is yours — accessible, growing, and on your terms. That’s not a pitch. That’s just what happens when you stop outsourcing the most important part of your financial life.",
  },
];

export const Problem = () => {
  const [openSlug, setOpenSlug] = useState(null);

  return (
    <section
      id="problem"
      data-testid={TEST_IDS.problem.section}
      className="bg-paper py-28 md:py-40"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="max-w-3xl">
          <p className="eyebrow mb-7">The Quiet Problem</p>
          <h2
            className="font-serif text-nearblack leading-[1.06] text-balance"
            style={{ fontSize: "clamp(1.95rem, 3.6vw, 3.4rem)", fontWeight: 400 }}
          >
            Most people were taught how to{" "}
            <em className="italic text-camel">make</em> money.
            <br />
            Few were taught how to{" "}
            <em className="italic text-camel">structure</em> it.
          </h2>
          <p className="mt-7 text-camel/85 text-[11px] tracking-[0.22em] uppercase font-medium">
            Click each item to read more
          </p>
        </div>

        {/* Cards mirror the Pillars structure: full-card click target with hover glow */}
        <div className="mt-20 md:mt-24 grid grid-cols-1 md:grid-cols-3 gap-px bg-paperdeep">
          {pains.map(({ n, slug, title, body, expanded }) => {
            const isOpen = openSlug === slug;
            return (
              <button
                key={slug}
                type="button"
                onClick={() => setOpenSlug(isOpen ? null : slug)}
                aria-expanded={isOpen}
                aria-controls={`problem-panel-${slug}`}
                data-testid={`problem-${slug}`}
                className={`text-left bg-paper p-10 md:p-12 group transition-colors cursor-pointer focus:outline-none focus-visible:ring-1 focus-visible:ring-camel ${
                  isOpen ? "bg-paperdeep/60" : "hover:bg-paperdeep/60"
                }`}
              >
                <div className="flex items-baseline justify-between mb-12">
                  <span className="font-serif italic text-camel text-xl">{n}</span>
                  <span className="block h-px bg-camel/40 flex-1 mx-6" />
                  <Plus
                    className={`w-4 h-4 text-camel transition-transform duration-300 ${
                      isOpen ? "rotate-45" : "rotate-0"
                    }`}
                    strokeWidth={1.5}
                    aria-hidden="true"
                  />
                </div>
                <h3
                  className="font-serif text-nearblack leading-tight"
                  style={{ fontSize: "clamp(1.5rem, 2.2vw, 1.85rem)", fontWeight: 400 }}
                >
                  {title}
                </h3>
                <p className="mt-6 text-nearblack/70 leading-[1.8] text-[15px] font-light">
                  {body}
                </p>

                {/* Expandable panel */}
                <div
                  id={`problem-panel-${slug}`}
                  data-testid={`problem-panel-${slug}`}
                  className={`grid transition-all duration-500 ease-out ${
                    isOpen ? "grid-rows-[1fr] opacity-100 mt-7" : "grid-rows-[0fr] opacity-0 mt-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="pt-6 border-t border-camel/40">
                      <p className="text-nearblack/85 leading-[1.85] text-[15px] font-light">
                        {expanded}
                      </p>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-16 md:mt-20 pt-10 border-t border-paperdeep flex items-center gap-6">
          <span
            className="inline-block divider-gold"
            style={{ width: "48px" }}
            aria-hidden="true"
          />
          <p className="font-serif italic text-nearblack text-2xl md:text-[2rem] leading-snug">
            The issue is rarely income. It’s structure.
          </p>
        </div>
      </div>
    </section>
  );
};
